import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus, Pencil, Trash2, RotateCw, ChevronDown, ChevronRight } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { DatePicker } from "@/components/ui/DatePicker";
import {
  useCredentialList,
  useCreateCredential,
  useUpdateCredential,
  useDeleteCredential,
  useMarkRotated,
} from "../hooks/useCredentials";
import type { CredentialItem, CredentialItemFormData, CredentialCategory, CredentialEnvironment } from "../types";

// ── constants ─────────────────────────────────────────────────────────────────

const CATEGORY_LABELS: Record<CredentialCategory, string> = {
  secret_key: "Klucz API / Secret",
  jwt: "JWT Secret",
  ssl_cert: "Certyfikat SSL/TLS",
  service_account: "Konto serwisowe",
  db_password: "Hasło bazy danych",
  oauth: "Token OAuth",
  sms_api: "Klucz API SMS",
  aws: "Poświadczenia AWS",
  email: "E-mail / SMTP",
  env_var: "Zmienna środowiskowa",
  other: "Inne",
};

const ENV_LABELS: Record<CredentialEnvironment, string> = {
  local: "Lokalne",
  production: "Produkcja",
  both: "Lokalne + Prod",
};

const STATUS_CONFIG = {
  ok: { label: "OK", color: "green" as const },
  due_soon: { label: "Wkrótce", color: "yellow" as const },
  overdue: { label: "Przeterminowane", color: "red" as const },
  unknown: { label: "Nieznany", color: "gray" as const },
};

// ── form schema ───────────────────────────────────────────────────────────────

const schema = z.object({
  name: z.string().min(1, "Nazwa jest wymagana").max(200),
  description: z.string(),
  rotation_instructions: z.string(),
  forced_rotation_reason: z.string(),
  category: z.enum([
    "secret_key", "jwt", "ssl_cert", "service_account", "db_password",
    "oauth", "sms_api", "aws", "email", "env_var", "other",
  ]),
  environment: z.enum(["local", "production", "both"]),
  last_rotated_at: z.string().nullable(),
  rotation_interval_days: z.number().int().positive().nullable(),
  is_active: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

// ── helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("pl-PL");
}

// ── form modal ────────────────────────────────────────────────────────────────

interface CredentialFormProps {
  item?: CredentialItem;
  onClose: () => void;
}

function CredentialFormModal({ item, onClose }: CredentialFormProps) {
  const create = useCreateCredential();
  const update = useUpdateCredential(item?.id ?? "");
  const isPending = create.isPending || update.isPending;

  const { register, handleSubmit, control, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: item
      ? {
          name: item.name,
          description: item.description,
          rotation_instructions: item.rotation_instructions,
          forced_rotation_reason: item.forced_rotation_reason,
          category: item.category,
          environment: item.environment,
          last_rotated_at: item.last_rotated_at ?? null,
          rotation_interval_days: item.rotation_interval_days,
          is_active: item.is_active,
        }
      : {
          name: "",
          description: "",
          rotation_instructions: "",
          forced_rotation_reason: "",
          category: "other",
          environment: "both",
          last_rotated_at: null,
          rotation_interval_days: null,
          is_active: true,
        },
  });

  const onSubmit = async (values: FormValues) => {
    const payload: CredentialItemFormData = {
      ...values,
      rotation_interval_days: values.rotation_interval_days ?? null,
      last_rotated_at: values.last_rotated_at || null,
    };
    if (item) {
      await update.mutateAsync(payload);
    } else {
      await create.mutateAsync(payload);
    }
    onClose();
  };

  const fieldCls = "w-full border border-[var(--color-border)] bg-[var(--color-bg-surface)] rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] text-[var(--color-text-primary)]";
  const labelCls = "block text-xs font-medium text-[var(--color-text-secondary)] mb-1";
  const errCls = "text-xs text-red-500 mt-1";

  return (
    <Modal open onClose={onClose} title={item ? "Edytuj wpis" : "Nowy wpis"} size="lg">
      <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4 overflow-y-auto">
        {/* name */}
        <div>
          <label className={labelCls}>Nazwa *</label>
          <input {...register("name")} className={fieldCls} placeholder="np. JWT SECRET_KEY" />
          {errors.name && <p className={errCls}>{errors.name.message}</p>}
        </div>

        {/* category + environment */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Kategoria</label>
            <select {...register("category")} className={fieldCls}>
              {(Object.entries(CATEGORY_LABELS) as [CredentialCategory, string][]).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Środowisko</label>
            <select {...register("environment")} className={fieldCls}>
              {(Object.entries(ENV_LABELS) as [CredentialEnvironment, string][]).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>
        </div>

        {/* description */}
        <div>
          <label className={labelCls}>Opis (co to jest i po co)</label>
          <textarea
            {...register("description")}
            rows={3}
            className={`${fieldCls} resize-y`}
            placeholder="Krótki opis czego dotyczy ten sekret / certyfikat / klucz..."
          />
        </div>

        {/* rotation_instructions */}
        <div>
          <label className={labelCls}>Instrukcja rotacji (krok po kroku)</label>
          <textarea
            {...register("rotation_instructions")}
            rows={6}
            className={`${fieldCls} resize-y font-mono text-xs`}
            placeholder={"1. Wygeneruj nowy klucz: ...\n2. Zaktualizuj w .env i Terraform.\n3. Zrestartuj backend."}
          />
        </div>

        {/* forced_rotation_reason */}
        <div>
          <label className={labelCls}>Powód wymuszenia rotacji</label>
          <textarea
            {...register("forced_rotation_reason")}
            rows={3}
            className={`${fieldCls} resize-y`}
            placeholder="Np. tokeny JWT ważne 7 dni — rotacja co 90 dni dla bezpieczeństwa. Lub: certyfikat Let's Encrypt wygasa co 90 dni."
          />
        </div>

        {/* interval + last_rotated_at */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Ostatnia rotacja</label>
            <Controller
              name="last_rotated_at"
              control={control}
              render={({ field }) => (
                <DatePicker value={field.value} onChange={field.onChange} />
              )}
            />
          </div>
          <div>
            <label className={labelCls}>Interwał rotacji (dni)</label>
            <input
              {...register("rotation_interval_days", { setValueAs: (v) => v === "" ? null : Number(v) })}
              type="number"
              min={1}
              className={fieldCls}
              placeholder="np. 365"
            />
            {errors.rotation_interval_days && (
              <p className={errCls}>{errors.rotation_interval_days.message}</p>
            )}
          </div>
        </div>

        {/* is_active */}
        <div className="flex items-center gap-2">
          <input {...register("is_active")} type="checkbox" id="is_active" className="w-4 h-4" />
          <label htmlFor="is_active" className="text-sm text-[var(--color-text-primary)]">
            Aktywny wpis
          </label>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={isPending}>
            Anuluj
          </Button>
          <Button type="submit" disabled={isPending}>
            {isPending ? "Zapisywanie..." : item ? "Zapisz zmiany" : "Dodaj"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ── expanded details (shared by row + card) ───────────────────────────────────

function CredentialDetails({ item }: { item: CredentialItem }) {
  return (
    <div className="space-y-4">
      {item.description && (
        <div>
          <p className="text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide mb-1">
            Opis
          </p>
          <p className="text-sm text-[var(--color-text-primary)]">{item.description}</p>
        </div>
      )}
      {item.forced_rotation_reason && (
        <div>
          <p className="text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide mb-1">
            Powód wymuszenia rotacji
          </p>
          <p className="text-sm text-[var(--color-text-primary)]">{item.forced_rotation_reason}</p>
        </div>
      )}
      {item.rotation_instructions && (
        <div>
          <p className="text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide mb-1">
            Instrukcja rotacji
          </p>
          <pre className="text-xs font-mono bg-[var(--color-bg-surface)] border border-[var(--color-border)] rounded p-3 whitespace-pre-wrap break-words text-[var(--color-text-primary)]">
            {item.rotation_instructions}
          </pre>
        </div>
      )}
    </div>
  );
}

// ── action buttons (shared) ───────────────────────────────────────────────────

interface ActionsProps {
  item: CredentialItem;
  onEdit: () => void;
  onDelete: () => void;
  size?: "sm" | "md";
}

function CredentialActions({ item, onEdit, onDelete, size = "sm" }: ActionsProps) {
  const markRotated = useMarkRotated();
  const iconSize = size === "md" ? 16 : 14;
  const btnCls = size === "md" ? "p-2" : "p-1";

  return (
    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <button
        className={`${btnCls} rounded hover:bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)]`}
        title="Oznacz jako zrotowany dziś"
        onClick={() => markRotated.mutate(item.id)}
        disabled={markRotated.isPending}
      >
        <RotateCw size={iconSize} />
      </button>
      <button
        className={`${btnCls} rounded hover:bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)]`}
        title="Edytuj"
        onClick={onEdit}
      >
        <Pencil size={iconSize} />
      </button>
      <button
        className={`${btnCls} rounded hover:bg-red-50 text-red-500`}
        title="Usuń"
        onClick={onDelete}
      >
        <Trash2 size={iconSize} />
      </button>
    </div>
  );
}

// ── expandable row (desktop) ──────────────────────────────────────────────────

interface RowProps {
  item: CredentialItem;
  onEdit: () => void;
  onDelete: () => void;
}

function CredentialRow({ item, onEdit, onDelete }: RowProps) {
  const [open, setOpen] = useState(false);
  const statusCfg = STATUS_CONFIG[item.rotation_status];

  return (
    <>
      <tr
        className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-elevated)] transition-colors cursor-pointer"
        onClick={() => setOpen((p) => !p)}
      >
        <td className="px-4 py-3 w-8">
          {open ? <ChevronDown size={14} className="text-[var(--color-text-secondary)]" /> : <ChevronRight size={14} className="text-[var(--color-text-secondary)]" />}
        </td>
        <td className="px-4 py-3 font-medium text-[var(--color-text-primary)] text-sm">
          {item.name}
          {!item.is_active && <span className="ml-2 text-xs text-[var(--color-text-secondary)]">(nieaktywny)</span>}
        </td>
        <td className="px-4 py-3 text-sm text-[var(--color-text-secondary)]">
          {CATEGORY_LABELS[item.category]}
        </td>
        <td className="px-4 py-3 text-sm text-[var(--color-text-secondary)]">
          {ENV_LABELS[item.environment]}
        </td>
        <td className="px-4 py-3 text-sm text-[var(--color-text-secondary)]">
          {formatDate(item.last_rotated_at)}
        </td>
        <td className="px-4 py-3 text-sm text-[var(--color-text-secondary)]">
          {item.next_due_at ? formatDate(item.next_due_at) : "—"}
        </td>
        <td className="px-4 py-3">
          <Badge color={statusCfg.color}>{statusCfg.label}</Badge>
        </td>
        <td className="px-4 py-3 w-28">
          <CredentialActions item={item} onEdit={onEdit} onDelete={onDelete} />
        </td>
      </tr>
      {open && (
        <tr className="bg-[var(--color-bg-elevated)] border-b border-[var(--color-border)]">
          <td />
          <td colSpan={7} className="px-6 py-4">
            <CredentialDetails item={item} />
          </td>
        </tr>
      )}
    </>
  );
}

// ── expandable card (mobile) ──────────────────────────────────────────────────

function CredentialCard({ item, onEdit, onDelete }: RowProps) {
  const [open, setOpen] = useState(false);
  const statusCfg = STATUS_CONFIG[item.rotation_status];

  return (
    <div className="border border-[var(--color-border)] rounded-lg bg-[var(--color-bg-surface)] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="w-full text-left px-4 py-3 hover:bg-[var(--color-bg-elevated)] transition-colors"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              {open ? (
                <ChevronDown size={14} className="text-[var(--color-text-secondary)] shrink-0" />
              ) : (
                <ChevronRight size={14} className="text-[var(--color-text-secondary)] shrink-0" />
              )}
              <span className="font-medium text-sm text-[var(--color-text-primary)] break-words">
                {item.name}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 ml-5 text-xs text-[var(--color-text-secondary)]">
              <span>{CATEGORY_LABELS[item.category]}</span>
              <span>·</span>
              <span>{ENV_LABELS[item.environment]}</span>
              {!item.is_active && <span className="italic">(nieaktywny)</span>}
            </div>
          </div>
          <Badge color={statusCfg.color}>{statusCfg.label}</Badge>
        </div>
      </button>

      {open && (
        <div className="border-t border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-4 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
            <div>
              <p className="font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide mb-0.5">
                Ostatnia rotacja
              </p>
              <p className="text-[var(--color-text-primary)]">{formatDate(item.last_rotated_at)}</p>
            </div>
            <div>
              <p className="font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide mb-0.5">
                Następna rotacja
              </p>
              <p className="text-[var(--color-text-primary)]">
                {item.next_due_at ? formatDate(item.next_due_at) : "—"}
              </p>
            </div>
          </div>

          <CredentialDetails item={item} />

          <div className="flex justify-end pt-2 border-t border-[var(--color-border)]">
            <CredentialActions item={item} onEdit={onEdit} onDelete={onDelete} size="md" />
          </div>
        </div>
      )}
    </div>
  );
}

// ── main page ─────────────────────────────────────────────────────────────────

export function CredentialsPage() {
  const user = useAuthStore((s) => s.user);
  if (!user?.is_developer) return <Navigate to="/" replace />;

  const { data: items = [], isLoading } = useCredentialList();
  const deleteCredential = useDeleteCredential();

  const [formItem, setFormItem] = useState<CredentialItem | null | undefined>(undefined);
  const [deleteTarget, setDeleteTarget] = useState<CredentialItem | null>(null);

  const sorted = [...items].sort((a, b) => {
    const order = { overdue: 0, due_soon: 1, unknown: 2, ok: 3 };
    return order[a.rotation_status] - order[b.rotation_status];
  });

  const overdue = items.filter((i) => i.rotation_status === "overdue").length;
  const dueSoon = items.filter((i) => i.rotation_status === "due_soon").length;

  return (
    <div className="p-4 sm:p-6 max-w-[1400px] mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5 sm:mb-6">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-xl font-semibold text-[var(--color-text-primary)]">
            Rotacja sekretów i certyfikatów
          </h1>
          <p className="text-xs sm:text-sm text-[var(--color-text-secondary)] mt-1">
            Rejestr kluczy, tokenów i certyfikatów wymagających cyklicznej aktualizacji.
          </p>
        </div>
        <Button onClick={() => setFormItem(null)} className="w-full sm:w-auto shrink-0">
          <Plus size={16} className="mr-1" /> Dodaj wpis
        </Button>
      </div>

      {/* summary badges */}
      {(overdue > 0 || dueSoon > 0) && (
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4 sm:mb-5">
          {overdue > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              <span className="font-semibold">{overdue}</span> przeterminowane
            </div>
          )}
          {dueSoon > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700">
              <span className="font-semibold">{dueSoon}</span> wkrótce do rotacji
            </div>
          )}
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-[var(--color-text-secondary)]">Ładowanie...</p>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-[var(--color-text-secondary)]">
          <p className="text-sm">Brak wpisów. Dodaj pierwszy sekret lub certyfikat.</p>
        </div>
      ) : (
        <>
          {/* mobile: card list */}
          <div className="md:hidden space-y-3">
            {sorted.map((item) => (
              <CredentialCard
                key={item.id}
                item={item}
                onEdit={() => setFormItem(item)}
                onDelete={() => setDeleteTarget(item)}
              />
            ))}
          </div>

          {/* desktop: table */}
          <div className="hidden md:block border border-[var(--color-border)] rounded overflow-hidden">
            <table className="w-full text-left">
              <thead className="bg-[var(--color-bg-elevated)] border-b border-[var(--color-border)]">
                <tr>
                  <th className="px-4 py-3 w-8" />
                  <th className="px-4 py-3 text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide">
                    Nazwa
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide">
                    Kategoria
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide">
                    Środowisko
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide">
                    Ostatnia rotacja
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide">
                    Następna rotacja
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide">
                    Status
                  </th>
                  <th className="px-4 py-3 w-28" />
                </tr>
              </thead>
              <tbody>
                {sorted.map((item) => (
                  <CredentialRow
                    key={item.id}
                    item={item}
                    onEdit={() => setFormItem(item)}
                    onDelete={() => setDeleteTarget(item)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* create / edit modal */}
      {formItem !== undefined && (
        <CredentialFormModal
          item={formItem ?? undefined}
          onClose={() => setFormItem(undefined)}
        />
      )}

      {/* delete confirmation */}
      {deleteTarget && (
        <Modal open onClose={() => setDeleteTarget(null)} title="Usuń wpis" size="sm">
          <div className="p-6 space-y-4">
            <p className="text-sm text-[var(--color-text-primary)]">
              Czy na pewno chcesz usunąć wpis <strong>{deleteTarget.name}</strong>?
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
                Anuluj
              </Button>
              <Button
                variant="danger"
                disabled={deleteCredential.isPending}
                onClick={async () => {
                  await deleteCredential.mutateAsync(deleteTarget.id);
                  setDeleteTarget(null);
                }}
              >
                {deleteCredential.isPending ? "Usuwanie..." : "Usuń"}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
